export interface ThemeSummary {
  id: string;
  name: string;
  organization: number | null;
  createdAt?: string;
}

export interface ThemeStamp {
  themeId?: string;
  themeName?: string;
  themeCreatedAt?: string;
  appliedAt?: string;
  themeScope?: string;
}

export interface ThemeDiff {
  newClasses?: string[];
  conflicts?: string[];
  themeBuiltinSlots?: string[];
  missingFonts?: string[];
  hasPendingDraft?: boolean;
}

export interface ResolvedTheme {
  applied: boolean;
  name: string | null;
  createdAt: string | null;
  appliedAt: string | null;
  scope: string | null;
  deleted: boolean;
  modified: boolean | null;
}

export function resolveAppliedTheme(stamp: ThemeStamp | undefined | null, themes: ThemeSummary[] | undefined, modified: boolean | null = null): ResolvedTheme {
  if (!stamp) {
    return { applied: false, name: null, createdAt: null, appliedAt: null, scope: null, deleted: false, modified: false };
  }

  const live = Array.isArray(themes) ? themes.find((theme) => theme.id === stamp.themeId) : undefined;
  const deleted = Array.isArray(themes) && !live;

  return {
    applied: true,
    name: live?.name ?? stamp.themeName ?? null,
    createdAt: stamp.themeCreatedAt ?? null,
    appliedAt: stamp.appliedAt ?? null,
    scope: stamp.themeScope ?? null,
    deleted,
    modified: deleted ? false : modified,
  };
}

export const isSystemTheme = (theme: ThemeSummary | undefined | null): boolean => theme?.organization === null || theme?.organization === undefined;

const byName = (a: ThemeSummary, b: ThemeSummary) => a.name.localeCompare(b.name, 'sv');
const byNewest = (a: ThemeSummary, b: ThemeSummary) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));

type Comparator = (a: ThemeSummary, b: ThemeSummary) => number;

export function sortThemes(themes: ThemeSummary[], tab: string, direction: string): ThemeSummary[] {
  const descending = direction === 'desc';
  const flip = (compare: Comparator): Comparator => (descending ? compare : (a, b) => compare(b, a));
  const byType = (a: ThemeSummary, b: ThemeSummary) => (isSystemTheme(a) ? 0 : 1) - (isSystemTheme(b) ? 0 : 1);

  const comparators: Record<string, Comparator> = {
    created: (a, b) => flip(byNewest)(a, b) || byName(a, b),
    name: (a, b) => flip((x, y) => byName(y, x))(a, b) || byNewest(a, b),
    type: (a, b) => flip(byType)(a, b) || byNewest(a, b) || byName(a, b),
  };

  return [...themes].sort(comparators[tab] ?? comparators.created);
}

export function themeApplyQuestions(diff: ThemeDiff | undefined | null) {
  const newClasses = diff?.newClasses?.length ?? 0;
  const conflicts = diff?.conflicts?.length ?? 0;
  const builtinSlots = diff?.themeBuiltinSlots?.length ?? 0;
  const ownClasses = newClasses + conflicts;

  return {
    askApplyScope: ownClasses > 0 || builtinSlots > 0,
    askConflicts: conflicts > 0,
    noteNoConflicts: conflicts === 0 && newClasses > 0,
    noteNoThemeClasses: ownClasses === 0,
    noteFonts: (diff?.missingFonts?.length ?? 0) > 0,
    noteDraft: Boolean(diff?.hasPendingDraft),
  };
}
